import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Check, ArrowRight, Mail, Star, Crown, Gem, Shield, Zap, BarChart3, Users, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COVERS = [
  { name: 'Louis Vuitton', url: 'https://images.unsplash.com/photo-1692638861925-c573fe57ec30?w=800&q=80' },
  { name: 'Prada', url: 'https://images.unsplash.com/photo-1760820708737-d204f21bc602?w=800&q=80' },
  { name: 'Gucci', url: 'https://images.unsplash.com/photo-1651853082716-aa92960afc90?w=800&q=80' },
  { name: 'Hermès', url: 'https://images.unsplash.com/photo-1731855315987-9a8f4fa833e5?w=800&q=80' },
  { name: 'Chanel', url: 'https://images.unsplash.com/photo-1731855315921-7be11aa1879d?w=800&q=80' },
];

const PRICING_TIERS = [
  {
    id: 'essential',
    name: 'Essential',
    tagline: 'The Showcase',
    price: '$2,500',
    period: '/mo',
    description: 'Maintain brand presence and drive consistent, high-intent traffic to your official boutique.',
    icon: Star,
    stripeUrl: 'https://buy.stripe.com/eVq7sNgFf3pMf2f5eK38403',
    features: [
      'Verified Brand Profile with exclusive badge',
      '4 Featured Feed Posts per month',
      'Direct "Shop Now" e-commerce links',
      'Standard Analytics & monthly reports'
    ],
    accentColor: '#a0a0a0',
    borderColor: 'border-white/10 hover:border-white/30',
  },
  {
    id: 'popular',
    name: 'Mr Popular',
    tagline: 'The Partnership',
    price: '$7,500',
    period: '/mo',
    description: 'Our signature tier. High-engagement storytelling with dedicated brand ambassadors.',
    icon: Crown,
    stripeUrl: 'https://buy.stripe.com/eVq9AVbkV9Oa7zNcHc38404',
    features: [
      '3 Elite "Mr Popular" Ambassadors',
      'High-fidelity content creation',
      'Priority feed placement for 7 days',
      'Advanced conversion tracking',
      'Community styling trend insights'
    ],
    accentColor: '#C41E3A',
    borderColor: 'border-[#C41E3A]/50 hover:border-[#C41E3A]',
    highlighted: true,
  },
  {
    id: 'heritage',
    name: 'Heritage',
    tagline: 'The Integration',
    price: '$15,000',
    period: '/mo',
    description: 'A bespoke, multi-channel takeover for the ultimate luxury brand experience.',
    icon: Gem,
    stripeUrl: 'https://buy.stripe.com/8x27sNfBbaSe5rF8qW38405',
    features: [
      '30-day Homepage Hero Takeover',
      'Custom "Heritage Classics" content series',
      'Book integration & featured chapter',
      'Global newsletter amplification',
      'Quarterly executive strategy sessions'
    ],
    accentColor: '#d4af37',
    borderColor: 'border-[#d4af37]/30 hover:border-[#d4af37]/60',
  }
];

const TierCard = ({ tier }) => {
  const Icon = tier.icon;

  return (
    <div
      className={`relative flex flex-col bg-[#141414] border ${tier.borderColor} transition-all duration-500 group ${tier.highlighted ? 'lg:-mt-4 lg:mb-0' : ''}`}
      data-testid={`tier-${tier.id}`}
    >
      {tier.highlighted && (
        <div className="absolute -top-px left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${tier.accentColor}, transparent)` }} />
      )}

      {tier.highlighted && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1 bg-[#C41E3A] text-white text-[10px] uppercase tracking-[0.25em] font-medium">
          Recommended
        </div>
      )}

      <div className="p-8 md:p-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center border"
            style={{ borderColor: `${tier.accentColor}40`, backgroundColor: `${tier.accentColor}10` }}
          >
            <Icon className="w-6 h-6" style={{ color: tier.accentColor }} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: tier.accentColor }}>
            {tier.tagline}
          </p>
          <h3 className="text-2xl font-serif italic text-white mb-5">{tier.name}</h3>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-4xl md:text-5xl font-serif text-white">{tier.price}</span>
            <span className="text-sm text-gray-500">{tier.period}</span>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mb-8 leading-relaxed">
          {tier.description}
        </p>

        {/* Divider */}
        <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: `${tier.accentColor}40` }} />

        {/* Features */}
        <ul className="space-y-4 mb-10 flex-1">
          {tier.features.map((feature) => (
            <li key={`${tier.id}-${feature.substring(0, 20)}`} className="flex items-start gap-3">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tier.accentColor }} />
              <span className="text-sm text-gray-400">{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <a
          href={tier.stripeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`block w-full py-4 text-center text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 ${
            tier.highlighted
              ? 'bg-[#C41E3A] text-white hover:bg-[#A01830] hover:shadow-[0_0_30px_rgba(196,30,58,0.3)]'
              : 'bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20'
          }`}
          data-testid={`tier-cta-${tier.id}`}
        >
          Get Started
          <ArrowRight className="w-3.5 h-3.5 inline-block ml-2" />
        </a>
      </div>
    </div>
  );
};

const SubscribeSection = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? 'Please enter a valid email' : (detail || 'Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0d0d0d] border-t border-white/5">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Newsletter</p>
        <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-4">Stay Ahead of the Curve</h2>
        <p className="text-gray-500 leading-relaxed mb-10">
          Exclusive insights on luxury fashion trends, brand partnerships, and early access to community events.
        </p>
        <form onSubmit={handleSubscribe} className="flex gap-0 max-w-lg mx-auto" data-testid="advertise-subscribe-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            className="flex-1 bg-[#1a1a1a] border border-white/10 border-r-0 text-white text-sm px-5 py-4 placeholder:text-gray-600 focus:outline-none focus:border-white/30 transition-colors"
            data-testid="advertise-subscribe-email"
          />
          <button
            type="submit"
            disabled={submitting}
            className="bg-white text-black px-6 py-4 text-xs uppercase tracking-wider font-medium transition-colors hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50 shrink-0"
            data-testid="advertise-subscribe-btn"
          >
            {submitting ? '...' : (
              <>
                Subscribe
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

const AdvertisePage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="advertise-page">
      {/* Hero — Brand Collage with Obsidian overlay */}
      <div className="relative h-[70vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-5 grid-rows-1">
          {BRAND_COVERS.map((brand) => (
            <div key={brand.name} className="relative overflow-hidden">
              <img src={brand.url} alt={brand.name} className="w-full h-full object-cover opacity-40" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/50 to-[#0a0a0a]" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent mx-auto mb-8" />
          <p className="text-[10px] uppercase tracking-[0.4em] text-gray-400 mb-6">Media Kit 2026</p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif italic text-white mb-6 leading-tight">
            Meet Mr. Fashion
          </h1>
          <p className="text-lg md:text-xl text-gray-400 font-light tracking-wide max-w-2xl mx-auto">
            The Timeless Transition of Luxury:<br />
            From Heritage to Future
          </p>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent mx-auto mt-8" />
        </div>
      </div>

      {/* Mission — Obsidian */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">The Mission</p>
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-8">
            {'\u201C'}Where Heritage Meets<br />High-Conversion{'\u201D'}
          </h2>
          <p className="text-gray-500 leading-relaxed text-lg max-w-3xl mx-auto">
            Meet Mr. Fashion (<span className="text-white">www.meetmr.fashion</span>) is a premier digital ecosystem bridging historical luxury heritage and modern e-commerce. Based on insights from <em className="text-gray-400">{'\u201C'}The Art of Luxury Fashion,{'\u201D'}</em> we curate a space for the {'\u201C'}New Generation of Collectors{'\u201D'} across North America and Europe.
          </p>
          <p className="text-white/80 font-medium mt-8 text-lg">
            We don{'\u2019'}t just showcase collections; we integrate legacies into the digital lives of high-intent luxury consumers.
          </p>
        </div>
      </section>

      {/* Why Advertise — Chrome accents */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#111111] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">The Opportunity</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white">Why Advertise With Us?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Curated Audience', desc: 'Reach a verified community of HNW individuals and luxury enthusiasts in the US, UK, and EU.' },
              { icon: Zap, title: 'The "Mr Popular" Edge', desc: 'Our proprietary framework ensures influencer content is high-fidelity and aligned with your corporate brand standards.' },
              { icon: BarChart3, title: 'Direct E-commerce Pipeline', desc: 'Every "Look" in our feed is a direct, trackable pathway to your official online boutique.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-8 bg-[#0a0a0a] border border-white/5 hover:border-white/15 transition-all duration-500">
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-lg font-serif italic text-white mb-3">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — Obsidian with chrome borders */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Investment Tiers</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-4">Advertiser Pricing</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">Select the tier that aligns with your brand objectives. All tiers include dedicated account management.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {PRICING_TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} />
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#111111] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Specifications</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white">Technical Requirements</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { label: 'Imagery', value: 'Minimum 300 DPI', sub: 'JPG or PNG format' },
              { label: 'Video', value: '4K Resolution', sub: '9:16 (Story) or 4:5 (Feed)' },
              { label: 'Lead Time', value: '10–21 Business Days', sub: 'Based on tier selection' },
            ].map(({ label, value, sub }) => (
              <div key={label} className="text-center p-6 bg-[#0a0a0a] border border-white/5">
                <p className="text-gray-600 text-[10px] uppercase tracking-[0.25em] mb-3">{label}</p>
                <p className="text-white font-serif text-lg">{value}</p>
                <p className="text-gray-600 text-xs mt-1">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact — Obsidian boardroom */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent mx-auto mb-8" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Begin Your Partnership</p>
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-8">Secure Your Slot</h2>

          <div className="mb-12">
            <p className="text-white font-medium text-lg mb-1">Akeem Joseph Feiton Sr.</p>
            <p className="text-gray-500 italic mb-6">Founder, Meet Mr. Fashion</p>

            <a
              href="mailto:partnerships@meetmr.fashion"
              className="inline-flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
            >
              <Mail className="w-5 h-5" />
              <span className="text-lg tracking-wide">partnerships@meetmr.fashion</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8 border-t border-white/5">
            <a href="https://buy.stripe.com/eVq9AVbkV9Oa7zNcHc38404" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#C41E3A] text-white hover:bg-[#A01830] px-8 py-6 text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_30px_rgba(196,30,58,0.2)]">
                Start With Mr Popular
              </Button>
            </a>
            <Link to="/feed">
              <Button className="bg-white/5 text-white border border-white/10 hover:bg-white/10 px-8 py-6 text-xs uppercase tracking-[0.2em]">
                Explore Our Feed
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <SubscribeSection />

      {/* Footer Quote */}
      <section className="py-16 px-6 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-gray-600 text-sm italic">
            {'\u201C'}Luxury is not about being noticed. It is about being remembered.{'\u201D'}
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdvertisePage;
